/*
Copyright 2026 New Vector Ltd.

SPDX-License-Identifier: AGPL-3.0-only OR LicenseRef-Element-Commercial
Please see LICENSE in the repository root for full details.
*/

import {
  ConnectionState,
  type LocalTrack,
  type Room,
  Track,
} from "livekit-client";
import { logger } from "matrix-js-sdk/lib/logger";

import type { LiveKitRecoveryAdapter as LiveKitRecoveryAdapterContract } from "../recovery/CallRecoveryAdapters";
import { createAbortError, throwIfAborted } from "../recovery/abort";
import { callTraceJournal, describeTraceError, safeOrigin } from "../telemetry";
import {
  effectiveCallTransportMode,
  enableAutoTcpFallback,
} from "./iceTransport";
import type { SFUConfig } from "./openIDSFU";
import { SCREEN_SHARE_PUBLISH_OPTIONS } from "./options";

/**
 * Сколько ждём, пока соединение встанет, прежде чем считать попытку неудачной.
 *
 * Обычный вход занимает секунды; 12 — с запасом на медленную сеть. Меньше ставить нельзя:
 * преждевременный обрыв убил бы соединение, которое вот-вот встало бы. Срок нужен потому,
 * что при потерях на UDP `connect` не падает, а висит, и человек смотрит в «подключаюсь».
 */
export const AUTO_TCP_FALLBACK_TIMEOUT_MS = 12_000;

class ConnectTimeout extends Error {}

/**
 * Единственная попытка LiveKit connect без внешнего или скрытого retry.
 *
 * При виде связи «Автоматически» неудачная попытка включает путь поверх TLS: следующую
 * попытку штатный перезапуск проведёт уже только через него. Сами здесь не повторяем —
 * счёт попыток принадлежит перезапуску, а не этой функции.
 *
 * Причина в замере 18.09.2026: согласование ICE держится за UDP, даже когда тот отвечает
 * наполовину, и само на TCP не уходит; у CTO под туннелем это выглядело как звонок,
 * который не соединяется вовсе, а на пути поверх TLS соединение встало и не рвалось.
 */
export async function connectLiveKitOnce(
  room: Room,
  config: SFUConfig,
  signal?: AbortSignal,
): Promise<void> {
  if (signal) throwIfAborted(signal);
  // Срок ставим только там, где неудача даёт запасной путь: иначе мы просто обрывали бы
  // затянувшийся, но рабочий вход.
  const withDeadline = effectiveCallTransportMode() === "auto";
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    // Форсируем relay: медиа всегда через ретранслятор одним путём, а не прямым P2P.
    // У пользователей с тоннелем прямой путь «прыгает» между адресами, ICE переназначает
    // пару и звонок рвётся. Так же поступает Телемост.
    const connect = room.connect(config.url, config.jwt, {
      maxRetries: 1,
      rtcConfig: { iceTransportPolicy: "relay" },
    });
    if (withDeadline) {
      await Promise.race([
        connect,
        new Promise<never>((_resolve, reject) => {
          timer = setTimeout(
            () => reject(new ConnectTimeout()),
            AUTO_TCP_FALLBACK_TIMEOUT_MS,
          );
        }),
      ]);
    } else {
      await connect;
    }
  } catch (error) {
    if (signal?.aborted) {
      await cleanupCancelledConnect(room);
      throw createAbortError();
    }
    if (withDeadline && enableAutoTcpFallback()) {
      callTraceJournal.record({
        eventType: "livekit.transport_fallback",
        stage: "livekit",
        outcome: "started",
        payload: {
          fallbackReason:
            error instanceof ConnectTimeout
              ? ("timeout" as const)
              : ("connect_failed" as const),
          timeoutMs: AUTO_TCP_FALLBACK_TIMEOUT_MS,
          ...describeTraceError(error),
        },
      });
      logger.warn(
        "Соединение по обычному пути не встало — следующая попытка только поверх TLS",
        error,
      );
      // Повисшую попытку закрываем: иначе следующая пойдёт поверх старого состояния.
      if (error instanceof ConnectTimeout) await cleanupCancelledConnect(room);
    }
    throw error;
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }

  if (signal?.aborted) {
    // LiveKit connect не принимает AbortSignal. Поздний успешный результат обязательно
    // закрываем, чтобы отменённая epoch не протекла в звонок.
    await cleanupCancelledConnect(room);
    throw createAbortError();
  }
}

async function cleanupCancelledConnect(room: Room): Promise<void> {
  try {
    await room.disconnect();
  } catch (error) {
    // Cleanup failure не должна превращать отменённую epoch в retryable
    // transport failure. Секреты в этом сообщении не участвуют.
    logger.warn("Не удалось закрыть отменённое подключение LiveKit", error);
  }
}

/**
 * Публикует подготовленные tracks после connect. Ошибка обязательного
 * микрофона пробрасывается, screen share сохраняет legacy best-effort policy.
 */
export async function publishPreparedLiveKitTracks(
  room: Room,
  micTrack: LocalTrack | undefined,
  screenshareTracks: readonly MediaStreamTrack[],
): Promise<void> {
  if (micTrack) {
    const startedAt = performance.now();
    const relatedEventId = callTraceJournal.record({
      eventType: "media.track_publish_started",
      stage: "media",
      outcome: "started",
      payload: { kind: "audio", source: "microphone" },
    });
    try {
      await room.localParticipant.publishTrack(micTrack, {
        source: Track.Source.Microphone,
      });
      callTraceJournal.record({
        eventType: "media.track_publish_succeeded",
        stage: "media",
        outcome: "succeeded",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: { kind: "audio", source: "microphone" },
      });
    } catch (error) {
      callTraceJournal.record({
        eventType: "media.track_publish_failed",
        stage: "media",
        severity: "error",
        outcome: "failed",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: {
          kind: "audio",
          source: "microphone",
          ...describeTraceError(error),
        },
      });
      throw error;
    }
  }

  for (const track of screenshareTracks) {
    room.localParticipant
      .publishTrack(track, {
        source: Track.Source.ScreenShare,
        ...SCREEN_SHARE_PUBLISH_OPTIONS,
      })
      .catch((error) => {
        logger.error("Failed to publish screenshare track", error);
      });
  }
}

/**
 * Adapter владеет операциями над переданной Room, но не создаёт и не заменяет
 * её. Каждый connect использует config текущего вызова.
 */
export class DefaultLiveKitRecoveryAdapter
  implements LiveKitRecoveryAdapterContract
{
  public constructor(private readonly room: Room) {}

  public async disconnectEpoch(signal: AbortSignal): Promise<void> {
    throwIfAborted(signal);
    await this.room.disconnect();
    throwIfAborted(signal);
  }

  public async connectFresh(
    config: SFUConfig,
    signal: AbortSignal,
  ): Promise<void> {
    const startedAt = performance.now();
    const relatedEventId = callTraceJournal.record({
      eventType: "livekit.connect_started",
      stage: "livekit",
      outcome: "started",
      payload: {
        attempt: 1,
        maxAttempts: 1,
        livekitOrigin: safeOrigin(config.url),
      },
    });
    try {
      await connectLiveKitOnce(this.room, config, signal);
      callTraceJournal.record({
        eventType: "livekit.connect_succeeded",
        stage: "livekit",
        outcome: "succeeded",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: { attempt: 1 },
      });
    } catch (error) {
      callTraceJournal.record({
        eventType: "livekit.connect_failed",
        stage: "livekit",
        severity: "error",
        outcome: "failed",
        relatedEventId,
        durationMs: Math.round(performance.now() - startedAt),
        payload: { attempt: 1, ...describeTraceError(error) },
      });
      throw error;
    }
  }

  public isConnected(): boolean {
    return this.room.state === ConnectionState.Connected;
  }
}
